import { z } from 'zod';
import {
  CALCULATION_MODES,
  CROP_METADATA_SCHEMA_VERSION,
  IMAGE_CATEGORIES,
  LIMITS,
  OCR_SCAN_MODES,
  OCR_SCAN_STRATEGIES,
  SCALE_PRECISION_OPTIONS,
  SOURCE_TYPES,
} from './constants.js';

/**
 * Runtime validation schemas shared between client and server.
 * The server treats these as the authoritative gate on all input; the client
 * uses them for immediate feedback. Never trust client-side validation alone.
 */

/** Optional trimmed string that becomes `undefined` when blank. */
const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' || v === undefined ? undefined : v));

const thcPercent = z
  .number({ invalid_type_error: 'THC percentage must be a number' })
  .min(LIMITS.THC_MIN, `THC must be at least ${LIMITS.THC_MIN}`)
  .max(LIMITS.THC_MAX, `THC must be at most ${LIMITS.THC_MAX}`);

const cbdPercent = z
  .number()
  .min(LIMITS.CBD_MIN)
  .max(LIMITS.CBD_MAX)
  .optional();

/** Optional minor cannabinoid % (same bounds as CBD). */
const optionalCannabinoidPercent = z
  .number()
  .min(LIMITS.CBD_MIN)
  .max(LIMITS.CBD_MAX)
  .optional();

const terpenesPercent = z
  .number()
  .min(LIMITS.TERPENES_MIN)
  .max(LIMITS.TERPENES_MAX)
  .optional();

/** ISO-ish date string (YYYY-MM-DD) or undefined. */
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
  .optional()
  .or(z.literal('').transform(() => undefined));

/**
 * Input schema for creating/updating a strain.
 * Requires a name (or product name) and a THC percentage.
 */
export const strainInputSchema = z
  .object({
    name: optionalTrimmed(LIMITS.NAME_MAX),
    productName: optionalTrimmed(LIMITS.NAME_MAX),
    producer: optionalTrimmed(LIMITS.PRODUCER_MAX),
    brand: optionalTrimmed(LIMITS.PRODUCER_MAX),
    category: optionalTrimmed(LIMITS.NAME_MAX),
    cultivarType: optionalTrimmed(LIMITS.NAME_MAX),
    /** Manual / free-text strain class (not a spreadsheet class ID). */
    strainClassSourceText: optionalTrimmed(LIMITS.NAME_MAX),
    /** Manual / free-text lineage. */
    lineageSourceText: optionalTrimmed(LIMITS.NAME_MAX),
    thcPercent,
    cbdPercent,
    cbnPercent: optionalCannabinoidPercent,
    cbgPercent: optionalCannabinoidPercent,
    thcvPercent: optionalCannabinoidPercent,
    cbcPercent: optionalCannabinoidPercent,
    totalTerpenesPercent: terpenesPercent,
    packageDate: dateString,
    purchaseDate: dateString,
    lotNumber: optionalTrimmed(LIMITS.LOT_MAX),
    sourceType: z.enum(SOURCE_TYPES).optional().default('local_manual'),
    notes: optionalTrimmed(LIMITS.NOTES_MAX),
    referenceNotes: optionalTrimmed(LIMITS.NOTES_MAX),
    isVerified: z.boolean().optional().default(false),
  })
  .refine((data) => Boolean(data.name) || Boolean(data.productName), {
    message: 'Either a display name or a product name is required',
    path: ['name'],
  });

export type StrainInput = z.infer<typeof strainInputSchema>;

/** Partial update; still requires that at least one field is present. */
export const strainUpdateSchema = strainInputSchema;

export const imageCategorySchema = z.enum(IMAGE_CATEGORIES);

/** Query parameters for listing/filtering the catalogue. */
export const strainQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  category: z.string().trim().max(200).optional(),
  producer: z.string().trim().max(200).optional(),
  /** Spreadsheet-managed vs local (null productId / non-spreadsheet source). */
  source: z.enum(['all', 'spreadsheet', 'local']).optional().default('all'),
  verified: z.enum(['all', 'verified', 'unverified']).optional().default('all'),
  archived: z.enum(['active', 'archived', 'all']).optional().default('active'),
  sort: z
    .enum(['name', 'thc_desc', 'thc_asc', 'recent'])
    .optional()
    .default('recent'),
});

export type StrainQuery = z.infer<typeof strainQuerySchema>;

/** A single strain participating in a blend calculation (potency snapshot). */
export const blendStrainInputSchema = z.object({
  strainId: z.string().min(1).optional(),
  name: z.string().min(1).max(LIMITS.NAME_MAX),
  producer: z.string().max(LIMITS.PRODUCER_MAX).optional(),
  thcPercent,
});

export type BlendStrainInput = z.infer<typeof blendStrainInputSchema>;

/** Request body for a blend calculation. */
export const blendCalculationRequestSchema = z
  .object({
    strains: z.array(blendStrainInputSchema).min(2, 'Select at least two strains'),
    totalWeightGrams: z.number().positive('Total weight must be greater than zero'),
    targetThcMg: z.number().min(0, 'Target THC cannot be negative'),
    scalePrecisionGrams: z
      .number()
      .positive('Scale precision must be greater than zero'),
    mode: z.enum(CALCULATION_MODES).optional().default('closest'),
    rangeMinMg: z.number().min(0).optional(),
    rangeMaxMg: z.number().min(0).optional(),
    maxThcMg: z.number().min(0).optional(),
  })
  .refine(
    (data) =>
      data.mode !== 'target_range' ||
      (data.rangeMinMg !== undefined &&
        data.rangeMaxMg !== undefined &&
        data.rangeMaxMg >= data.rangeMinMg),
    {
      message:
        'Target range mode requires a minimum and maximum, with max ≥ min',
      path: ['rangeMaxMg'],
    },
  );

export type BlendCalculationRequest = z.infer<
  typeof blendCalculationRequestSchema
>;

/** Persisted blend calculation (snapshot). */
export const blendHistoryInputSchema = z.object({
  request: blendCalculationRequestSchema,
  note: z.string().trim().max(LIMITS.NOTES_MAX).optional(),
});

export type BlendHistoryInput = z.infer<typeof blendHistoryInputSchema>;

/** Application settings. */
export const settingsSchema = z.object({
  defaultBlendWeightGrams: z.number().positive(),
  defaultTargetThcMg: z.number().min(0),
  defaultScalePrecisionGrams: z.number().positive(),
  enableCalculationHistory: z.boolean(),
  preserveOriginalImages: z.boolean(),
  theme: z.enum(['system', 'light', 'dark']),
});

export type Settings = z.infer<typeof settingsSchema>;

export const settingsUpdateSchema = settingsSchema.partial();

export type SettingsUpdate = z.infer<typeof settingsUpdateSchema>;

/** Validates that a scale precision value is one we understand. */
export const scalePrecisionSchema = z
  .number()
  .positive()
  .refine(
    (v) => SCALE_PRECISION_OPTIONS.includes(v as (typeof SCALE_PRECISION_OPTIONS)[number]) || v > 0,
    { message: 'Unsupported scale precision' },
  );

export const ocrScanModeSchema = z.enum(OCR_SCAN_MODES);
export const ocrScanStrategySchema = z.enum(OCR_SCAN_STRATEGIES);

export const cropRectSchema = z.object({
  x: z.number().finite().min(0),
  y: z.number().finite().min(0),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
});

export const cropMetadataSchema = z.object({
  schemaVersion: z.literal(CROP_METADATA_SCHEMA_VERSION),
  crop: cropRectSchema,
  rotation: z.union([
    z.literal(0),
    z.literal(90),
    z.literal(180),
    z.literal(270),
  ]),
  sourceWidth: z.number().int().positive(),
  sourceHeight: z.number().int().positive(),
  scanMode: ocrScanModeSchema,
});

export type CropMetadataInput = z.infer<typeof cropMetadataSchema>;

/** Optional JSON array of OCR field kinds for category-prioritized parsing. */
const ocrFieldKindSchema = z.enum([
  'name',
  'productName',
  'producer',
  'thcPercent',
  'totalThcMg',
  'thcMgPerGram',
  'cbdPercent',
  'totalCbdMg',
  'cbdMgPerGram',
  'netWeightGrams',
  'packageDate',
  'lotNumber',
  'terpenesPercent',
  'category',
  'note',
]);

/**
 * Multipart form fields accompanying an OCR analyze upload (aside from the
 * image file). Designed so Phase B staged variants fit without breaking clients.
 */
export const ocrAnalyzeFormSchema = z.object({
  scanMode: ocrScanModeSchema.default('full'),
  strategy: ocrScanStrategySchema.default('standard'),
  cropMetadata: cropMetadataSchema.optional(),
  /** Guided capture category — used to prioritize / filter field candidates. */
  imageCategory: imageCategorySchema.optional(),
  /**
   * Intended extraction fields for this image. When omitted but imageCategory
   * is known, the server fills from package-capture step definitions.
   */
  intendedFields: z.array(ocrFieldKindSchema).optional(),
});

export type OcrAnalyzeForm = z.infer<typeof ocrAnalyzeFormSchema>;
